import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import CandidateFormSections from '../components/CandidateFormSections';
import FormSection, { FieldRow } from '../components/FormSection';
import PasswordField from '../components/PasswordField';
import CodeInput from '../components/CodeInput';
import { Alert, SubmitButton } from '../components/Feedback';
import { usePspAuth } from '../context/PspAuthContext';
import { candidateToFormValues, useCandidateForm } from '../hooks/useCandidateForm';
import { isStrongPassword } from '../constants/options';
import { isValidEmail } from '../utils/masks';
import { candidateApi } from '../services/pspApi';

const EMPTY_PASSWORDS = { current_password: '', new_password: '', confirm_password: '' };

const Profile = () => {
    const { friendly_url } = useParams();
    const { candidate, setCandidate } = usePspAuth();
    const { values, setValues, errors, setErrors, setField } = useCandidateForm(candidateToFormValues(candidate));

    const [passwords, setPasswords] = useState(EMPTY_PASSWORDS);
    const [message, setMessage] = useState({ type: 'error', text: '' });
    const [loading, setLoading] = useState(false);

    // E-mail change confirmation
    const [pendingEmail, setPendingEmail] = useState(null);
    const [code, setCode] = useState('');
    const [codeMessage, setCodeMessage] = useState({ type: 'error', text: '' });
    const [confirming, setConfirming] = useState(false);
    const [resending, setResending] = useState(false);

    useEffect(() => {
        window.scrollTo({ top: 0 });
    }, [friendly_url]);

    const handlePassword = (name, value) => {
        setPasswords((current) => ({ ...current, [name]: value }));
        setErrors((current) => ({ ...current, [name]: undefined }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setMessage({ type: 'error', text: '' });

        const found = {};
        if (!isValidEmail(values.email)) found.email = 'Informe um e-mail válido.';
        if (passwords.new_password || passwords.current_password || passwords.confirm_password) {
            if (!passwords.current_password) found.current_password = 'Informe a senha atual.';
            if (!isStrongPassword(passwords.new_password)) found.new_password = 'A senha não atende aos requisitos.';
            if (passwords.new_password !== passwords.confirm_password) found.confirm_password = 'As senhas não conferem.';
        }
        if (Object.keys(found).length > 0) {
            setErrors(found);
            setMessage({ type: 'error', text: 'Verifique os campos informados.' });
            return;
        }

        setLoading(true);
        const payload = { ...values };
        if (passwords.new_password) {
            payload.current_password = passwords.current_password;
            payload.new_password = passwords.new_password;
            payload.confirm_password = passwords.confirm_password;
        }
        const result = await candidateApi.update(payload);
        setLoading(false);

        if (result && result.status === 1) {
            setCandidate(result.candidate);
            setPasswords(EMPTY_PASSWORDS);
            setErrors({});

            if (result.email_change_pending) {
                setPendingEmail(result.pending_email);
                setCode('');
                setCodeMessage({ type: 'info', text: `Enviamos um código de confirmação para ${result.pending_email}.` });
                setMessage({ type: 'success', text: 'Dados atualizados. Confirme o novo e-mail para concluir a alteração.' });
            } else {
                setPendingEmail(null);
                setMessage({ type: 'success', text: 'Dados atualizados com sucesso.' });
            }
        } else {
            setErrors((result && result.errors) || {});
            setMessage({ type: 'error', text: (result && result.message) || 'Não foi possível atualizar o perfil.' });
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleConfirmEmail = async (event) => {
        if (event) event.preventDefault();
        if (code.length !== 6) {
            setCodeMessage({ type: 'error', text: 'Informe o código de 6 dígitos.' });
            return;
        }

        setConfirming(true);
        const result = await candidateApi.confirmEmailChange(pendingEmail, code);
        setConfirming(false);

        if (result && result.status === 1) {
            setCandidate(result.candidate);
            setValues((current) => ({ ...current, email: result.candidate.email }));
            setPendingEmail(null);
            setMessage({ type: 'success', text: 'E-mail alterado com sucesso.' });
        } else {
            setCode('');
            setCodeMessage({ type: 'error', text: (result && result.message) || 'Não foi possível confirmar o código.' });
            if (result && result.restart) {
                setPendingEmail(null);
                setValues((current) => ({ ...current, email: candidate.email }));
                setMessage({ type: 'error', text: 'Não foi possível confirmar o novo e-mail. Solicite a alteração novamente.' });
            }
        }
    };

    const handleResend = async () => {
        setResending(true);
        const result = await candidateApi.resendCode(pendingEmail, 'troca_email');
        setResending(false);

        if (result && result.status === 1) {
            setCode('');
            setCodeMessage({ type: 'success', text: 'Enviamos um novo código.' });
        } else {
            setCodeMessage({ type: 'error', text: (result && result.message) || 'Não foi possível reenviar o código.' });
            if (result && result.restart) {
                setPendingEmail(null);
                setValues((current) => ({ ...current, email: candidate.email }));
            }
        }
    };

    if (!candidate) {
        return null;
    }

    return (
        <div className="psp-container psp-fade-in">
            <div className="psp-form-card">
                <div className="psp-form-card__header">
                    <h1>Meus Dados</h1>
                    <p>
                        Atualize suas informações. Campos marcados com <span className="psp-required">*</span> são obrigatórios.
                    </p>
                </div>

                <form className="psp-form-card__body" onSubmit={handleSubmit} noValidate>
                    <Alert type={message.type}>{message.text}</Alert>

                    <CandidateFormSections values={values} errors={errors} onChange={setField} />

                    {pendingEmail && (
                        <div className="psp-subsection psp-stack" aria-live="polite">
                            <strong>Confirmar novo e-mail ({pendingEmail})</strong>
                            <Alert type={codeMessage.type}>{codeMessage.text}</Alert>
                            <CodeInput value={code} onChange={setCode} disabled={confirming} />
                            <div className="psp-actions">
                                <button type="button" className="psp-btn psp-btn--primary" onClick={handleConfirmEmail} disabled={confirming}>
                                    Confirmar e-mail
                                </button>
                                <button type="button" className="psp-link" onClick={handleResend} disabled={resending}>
                                    {resending ? 'Reenviando...' : 'Reenviar código'}
                                </button>
                            </div>
                        </div>
                    )}

                    <FormSection title="6. Alterar Senha (opcional)">
                        <FieldRow index={1} label="Senha Atual" htmlFor="psp-current_password">
                            <PasswordField name="current_password" value={passwords.current_password} onChange={handlePassword} error={errors.current_password} autoComplete="current-password" />
                        </FieldRow>
                        <FieldRow index={2} label="Nova Senha" htmlFor="psp-new_password">
                            <PasswordField name="new_password" value={passwords.new_password} onChange={handlePassword} error={errors.new_password} showRules autoComplete="new-password" />
                        </FieldRow>
                        <FieldRow index={3} label="Confirmar Nova Senha" htmlFor="psp-confirm_password">
                            <PasswordField name="confirm_password" value={passwords.confirm_password} onChange={handlePassword} error={errors.confirm_password} autoComplete="new-password" />
                        </FieldRow>
                    </FormSection>

                    <SubmitButton loading={loading}>Salvar Alterações</SubmitButton>
                </form>
            </div>
        </div>
    );
};

export default Profile;
